import { Injectable, WritableSignal } from '@angular/core';
import { formatEther, Hex } from 'viem';
import { ExtendedMon, Type } from '../types/structs';
import { Effectiveness, TYPE_EFFECTIVENESS_CHART, typeData } from '../data/types';
import { Mon, Move, MoveClass } from '../generated/mon';
import { buildExtendedMon } from './build-extended-mon';
import type { BattleUiState } from './battle-ui-state.service';
import { OnlineUser } from '../types/server';
import { isCpuAddress } from '../data/address-config';

export function addressEquals(a: Hex | undefined, b: Hex | undefined): boolean {
  if (!a || !b) return false;
  return a.toLowerCase() === b.toLowerCase();
}

/** No styled 𝕏 mark — use this for text interpolation (battle prose, headers).
 *  For rich rendering, use `<app-identity>` instead. */
export function labelForPlayer(
  clientInfo: OnlineUser | undefined,
  fallback: string
): string {
  if (clientInfo?.twitterHandle) return `@${clientInfo.twitterHandle}`;
  return clientInfo?.username || fallback;
}

/** Every name form a log line may carry for one player. The prose resolves through
 *  `labelForPlayer` while server notices resolve through `formatPlayerName`, so both
 *  a handle and a username can reach the log for the same address. */
export function playerNameAliases(
  clientInfo: OnlineUser | undefined,
  selfUsername?: string
): string[] {
  const aliases: string[] = [];
  if (clientInfo?.twitterHandle) aliases.push(`@${clientInfo.twitterHandle}`);
  if (clientInfo?.username) aliases.push(clientInfo.username);
  if (selfUsername) aliases.push(selfUsername);
  return aliases;
}

// The one wei -> display-ETH formatter. Decimals vary by context (5 for a balance
// readout, 4 for a compact row, 6 for a small delta), so the caller picks.
export function formatEth(wei: bigint, decimals = 5): string {
  return Number(formatEther(wei)).toFixed(decimals);
}

export function shortenAddress(addr: Hex | string): string {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/** CPU opponents are labelled by callers from their difficulty tier — the
 *  collapsed CPU address can't encode it. */
/** Catalog move-class string ('Physical'/'Special'/'Status') → its display glyph. */
export function moveClassIconFor(moveClass: string): string {
  switch (moveClass?.toLowerCase()) {
    case 'physical': return '⚔';
    case 'special': return '✦';
    case 'status': return '◐';
    default: return '◯';
  }
}

export function formatPlayerName(input: {
  address: Hex | string;
  username?: string | null;
  twitterHandle?: string | null;
}): string {
  if (input.twitterHandle) return `@${input.twitterHandle}`;
  if (input.username) return input.username;
  return shortenAddress(input.address);
}

/** Shared display-name resolution: CPU label > @handle > username (selfUsername when
 *  addr is self, presence otherwise) > fallback (shortened address by default). */
export function resolveDisplayName(
  addr: Hex | string | undefined,
  opts: {
    lookupClient: (addr: Lowercase<Hex>) => OnlineUser | undefined;
    /** Called only when addr is a CPU opponent, so signal reads stay branch-scoped. */
    cpuLabel?: () => string;
    selfAddress?: string;
    selfUsername?: string;
    fallback?: string;
  },
): string {
  if (!addr) return '';
  const lower = addr.toLowerCase() as Lowercase<Hex>;
  if (opts.cpuLabel && isCpuAddress(lower)) return opts.cpuLabel();
  const online = opts.lookupClient(lower);
  const isSelf = !!opts.selfAddress && lower === opts.selfAddress.toLowerCase();
  const username = isSelf ? opts.selfUsername : online?.username;
  if (online?.twitterHandle) return `@${online.twitterHandle}`;
  if (username) return username;
  return opts.fallback ?? shortenAddress(addr);
}

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function hpPercent(mon: ExtendedMon): number {
  const hp = mon.stats.hp();
  if (hp < 0) return 0;
  return (hp / mon.stats.maxHp) * 100;
}

export function hpPercentLabel(mon: ExtendedMon): number {
  return Math.round(hpPercent(mon));
}

export function bigintReplacer(_key: string, value: unknown): unknown {
  return typeof value === 'bigint' ? value.toString() : value;
}

/** Parses a Solidity typehash string ("StructName(type1 field1,...)") into a
 *  structName + viem-compatible types map for signTypedData. */
export function parseTypehashToEip712Types(
  typehashString: string
): { structName: string; types: Record<string, readonly { name: string; type: string }[]> } {
  const match = typehashString.match(/^(\w+)\((.+)\)$/);
  if (!match) {
    throw new Error(`Invalid typehash string format: ${typehashString}`);
  }

  const structName = match[1];
  const fieldsString = match[2];

  const fields = fieldsString.split(',').map((field) => {
    const parts = field.trim().split(' ');
    if (parts.length !== 2) {
      throw new Error(`Invalid field format: ${field}`);
    }
    return { type: parts[0], name: parts[1] };
  });

  return {
    structName,
    types: { [structName]: fields },
  };
}

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  async copyWithFeedback(
    text: string,
    feedback: WritableSignal<string>,
    ms = 1200,
    copiedMessage = 'Copied!',
  ): Promise<void> {
    await navigator.clipboard.writeText(text);
    feedback.set(copiedMessage);
    setTimeout(() => feedback.set(''), ms);
  }

  flashFeedback(
    message: string,
    feedback: WritableSignal<string>,
    ms = 1200,
  ): void {
    feedback.set(message);
    setTimeout(() => feedback.set(''), ms);
  }

  getRngSaltForMove(): bigint {
    // uint104 — 13 random bytes assembled as a bigint matches the on-chain
    // SignedCommitLib salt width and the lane size used by MonMoves packing.
    return this.randomSalt(13);
  }

  getRngSaltForSlotSide(): bigint {
    // uint80 — 2-slot side wire words cap the salt at 80 bits so a staged
    // turn's two 128-bit words share one Engine buffer slot.
    return this.randomSalt(10);
  }

  private randomSalt(bytes: number): bigint {
    const rng = crypto.getRandomValues(new Uint8Array(bytes));
    let salt = 0n;
    for (const byte of rng) {
      salt = (salt << 8n) | BigInt(byte);
    }
    return salt;
  }

  // Commit-by-turnId-parity. The built-in flow routes every turn (single-player turns auto-NO_OP the idle
  // side) through commit→reveal→submit, so the switch flag no longer affects who commits.
  shouldPlayerCommit(
    turnId: number,
    playerIndex: number,
    _playerSwitchForTurnFlag: number
  ): boolean {
    return turnId % 2 === playerIndex;
  }

  getPrimaryTypeColor(type: Type): string;
  getPrimaryTypeColor(mon: Pick<Mon, 'type1' | 'type2'>): string;
  getPrimaryTypeColor(typeOrMon: Type | Pick<Mon, 'type1' | 'type2'>): string {
    if (typeof typeOrMon === 'object' && 'type1' in typeOrMon) {
      return typeData[typeOrMon.type1].bgColor;
    } else {
      return typeData[typeOrMon as Type].bgColor;
    }
  }

  getSecondaryTypeColor(type: Type): string;
  getSecondaryTypeColor(mon: Pick<Mon, 'type1' | 'type2'>): string;
  getSecondaryTypeColor(
    typeOrMon: Type | Pick<Mon, 'type1' | 'type2'>
  ): string {
    if (typeof typeOrMon === 'object' && 'type2' in typeOrMon) {
      return typeOrMon.type2 ? typeData[typeOrMon.type2].bgColor : '';
    } else {
      return typeData[typeOrMon as Type].bgColor;
    }
  }

  getMoveTypeColor(move: Move): string;
  getMoveTypeColor(type: Type): string;
  getMoveTypeColor(moveOrType: Move | Type): string {
    if (moveOrType && typeof moveOrType === 'object' && 'type' in moveOrType) {
      return typeData[moveOrType.type].bgColor;
    } else {
      return typeData[moveOrType as Type].bgColor;
    }
  }

  monToExtendedMon(mon: Mon): ExtendedMon {
    // Unfaceted, full catalog loadout — the dex view of a mon rather than a battle one.
    return buildExtendedMon(mon, undefined, 0, mon.moves);
  }

  getHpPercentage(mon: ExtendedMon): number {
    return hpPercent(mon);
  }

  getHpBarColor(mon: ExtendedMon): string {
    return this.getHpBarColorForPercent(this.getHpPercentage(mon));
  }

  getHpBarColorForPercent(percentage: number): string {
    if (percentage > 50) return 'var(--battle-hp-high)';
    if (percentage > 25) return 'var(--battle-hp-mid)';
    return 'var(--battle-hp-low)';
  }

  getMoveClassIcon(moveClass: MoveClass): string {
    switch (moveClass) {
      case MoveClass.Physical:
        return '⚔';
      case MoveClass.Special:
        return '✦';
      case MoveClass.Other:
        return '◐';
      case MoveClass.Self:
        return '◯';
      default:
        return '◯';
    }
  }

  getMoveClassShortened(moveClass: MoveClass): string {
    switch (moveClass) {
      case MoveClass.Physical:
        return 'PHYS';
      case MoveClass.Special:
        return 'SPEC';
      case MoveClass.Other:
        return 'OTHER';
      case MoveClass.Self:
        return 'SELF';
    }
  }

  getEffectivenessValue(state: BattleUiState, move: Move): Effectiveness {
    // No opponent on field (pre-reveal / transient replay-load window) → no badge.
    const opponentMon = state.opponentActiveMon();
    if (!opponentMon) return Effectiveness.Normal;
    return this.getEffectivenessAgainst(move, opponentMon);
  }

  getEffectivenessAgainst(move: Move, defender: ExtendedMon): Effectiveness {
    const multiplier1 = TYPE_EFFECTIVENESS_CHART[move.type as Type][defender.type1 as Type];
    if (!defender.type2) return multiplier1 as Effectiveness;
    const multiplier2 = TYPE_EFFECTIVENESS_CHART[move.type as Type][defender.type2 as Type];
    return (multiplier1 * multiplier2) as Effectiveness;
  }

}
